import { create } from "zustand";
import type { User } from "@shared/core/types";
import { api, ApiError } from "../lib/api";

export interface MfaSetup {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  /** Mot de passe accepté, code de double authentification attendu. */
  mfaPending: boolean;
  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  setupMfa: () => Promise<MfaSetup>;
  enableMfa: (code: string) => Promise<string[]>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  setUser: (user: User) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  loading: true,
  mfaPending: false,
  login: async (email, password) => {
    const r = await api<User | { mfaRequired: true }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if ("mfaRequired" in r) {
      set({ mfaPending: true });
      return;
    }
    set({ user: r, mfaPending: false });
  },
  verifyMfa: async (code) => {
    const u = await api<User>("/api/auth/mfa/verify", { method: "POST", body: JSON.stringify({ code }) });
    set({ user: u, mfaPending: false });
  },
  cancelMfa: () => set({ mfaPending: false }),
  setupMfa: () => api<MfaSetup>("/api/auth/mfa/setup", { method: "POST" }),
  enableMfa: async (code) => {
    const r = await api<{ user: User; recoveryCodes: string[] }>("/api/auth/mfa/enable", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
    set({ user: r.user });
    return r.recoveryCodes;
  },
  logout: async () => {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      // déjà déconnecté
    }
    set({ user: null, mfaPending: false });
  },
  refresh: async () => {
    try {
      const u = await api<User>("/api/auth/me");
      set({ user: u, loading: false });
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 401) console.error("auth refresh failed", e);
      set({ user: null, loading: false });
    }
  },
  setUser: (user) => set({ user }),
}));
